import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocation } from "api-types/locations/locationsResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { getPlanBasePrice } from "lib/components/builders/communities/utils/getCommunityPlanPrice";
import { useLocationHierarchy } from "lib/components/builders/communities/utils/useLocationHierarchy";
import PlanThumbnailCell from "lib/components/builders/PlanThumbnailCell";
import { getPlanStat, hasPlanStat } from "lib/house/stats/planStatsLookup";
import { useUserContext } from "lib/state/UserContextProvider";
import Checkbox from "ui/forms/CheckboxOld";
import Radio from "ui/forms/Radio";
import DataTable from "ui/tables/DataTable";
import type { DataTableColumn } from "ui/tables/DataTable/types";
import WarnOutOfDate from "./homePlans/WarnOutOfDate";
import styles from "./SelectPlansTable.module.scss";

export type ClientGenericPlan = ClientMasterPlan | ClientLocationPlan;

interface SelectPlansProps {
  multiSelect?: boolean;
  availablePlans: ClientGenericPlan[];
  location: ClientLocation | undefined;
  selectedIds: string[];
  onSelect: (id: string) => void;
  builderId: string;
  /** lookup for plan id → tooltip when some plans cannot be selected */
  disabledPlanReasons?: Record<string, string>;
  /** lookup for plan id → unit count; supply to show the Units column in multi-unit flows */
  unitCounts?: Record<string, number | undefined>;
}

export function SelectPlansTable({
  multiSelect,
  availablePlans,
  location,
  selectedIds,
  onSelect,
  builderId,
  disabledPlanReasons,
  unitCounts,
}: SelectPlansProps) {
  const { builder } = useUserContext();
  const { data: locationHierarchy } = useLocationHierarchy(builderId, location?.id ?? "");

  const getDisabledReason = (planId: string): string | undefined => disabledPlanReasons?.[planId];

  // DataTable has no row-level tooltip, so every column opts in to cover the whole row
  const disabledReasonTooltip: DataTableColumn<ClientGenericPlan>["tooltip"] = ({ row }) =>
    getDisabledReason(row.original.id);

  const showUnitsColumn = !!unitCounts;
  // Widths are fractions that must total 1 across visible columns, so Plan Name absorbs the Units
  // column's 0.08 when it is hidden.
  const planNameWidth = showUnitsColumn ? 0.6 : 0.68;

  const unitsColumn: DataTableColumn<ClientGenericPlan> = {
    header: "Units",
    id: "units",
    width: 0.08,
    maxSize: 80,
    tooltip: disabledReasonTooltip,
    accessorFn: (plan) => unitCounts?.[plan.id],
  };

  const columns: DataTableColumn<ClientGenericPlan>[] = [
    {
      header: "",
      id: "checkbox",
      width: 0.05,
      maxSize: 40,
      tooltip: disabledReasonTooltip,
      cell: function CheckboxCell({ row }) {
        const disabledReason = getDisabledReason(row.original.id);
        return (
          <div className={styles.checkboxCell} data-testid={`select-plan-${row.original.name}`}>
            {multiSelect ? (
              <Checkbox
                aria-label={`Include ${row.original.name}`}
                checked={selectedIds.includes(row.original.id)}
                onChange={() => onSelect(row.original.id)}
                disabled={!!disabledReason}
              />
            ) : (
              <Radio
                aria-label={`Include ${row.original.name}`}
                checked={selectedIds.includes(row.original.id)}
                onChange={() => onSelect(row.original.id)}
                disabled={!!disabledReason}
              />
            )}
          </div>
        );
      },
    },
    {
      header: "Plan Name",
      accessorKey: "name",
      width: planNameWidth,
      tooltip: disabledReasonTooltip,
      cell: function ThumbnailCell({ row }) {
        return (
          <div className={styles.planNameCell}>
            <PlanThumbnailCell plan={row.original} className={styles.planImage} />
            {row.original.name}
          </div>
        );
      },
      enableResizing: false,
      fullCell: true,
      id: "thumbnailUrl",
    },
    ...(showUnitsColumn ? [unitsColumn] : []),
    {
      header: "Base Price",
      width: 0.14,
      maxSize: 120,
      tooltip: disabledReasonTooltip,
      accessorFn: (plan) => {
        return getPlanBasePrice(plan, location, builder, locationHierarchy?.ancestors);
      },
    },
    {
      header: "SQFT",
      width: 0.13,
      maxSize: 130,
      tooltip: disabledReasonTooltip,
      accessorFn: (plan) =>
        getPlanStat(
          plan.stats,
          hasPlanStat(plan.stats, "conditionedFloorArea") ? "conditionedFloorArea" : "totalFloorArea",
        ),
      cell: function TotalFloorAreaCell({ row }) {
        const plan = row.original;
        let floorArea: string;
        let usesFallback = false;
        if (hasPlanStat(plan.stats, "conditionedFloorArea")) {
          floorArea = getPlanStat(plan.stats, "conditionedFloorArea");
        } else {
          floorArea = getPlanStat(plan.stats, "totalFloorArea");
          usesFallback = true;
        }

        return (
          <span>
            {floorArea} SQFT
            {usesFallback && <WarnOutOfDate />}
          </span>
        );
      },
    },
  ];

  return (
    <DataTable
      data={availablePlans}
      columns={columns}
      onRowClick={(row) => onSelect(row.original.id)}
      getRowCanClick={(row) => !getDisabledReason(row.original.id)}
      getRowClassNames={(row) => (getDisabledReason(row.original.id) ? styles.disabledRow : "")}
    />
  );
}
